import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { checkPaymentStatus } from '../../services/api';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [bookingId, setBookingId] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    verifyPayment();
    // eslint-disable-next-line
  }, []);

  const verifyPayment = async () => {
    try {
      // Get parameters from Netopia redirect URL
      const orderId = searchParams.get('orderId') || searchParams.get('order_id');
 
      if (!orderId) {
        setStatus('error');
        return;
      }

      setBookingId(orderId);

      // Verify payment status with backend
      const response = await checkPaymentStatus(orderId);
   

      setPaymentDetails(response.data);

      // Set status based on backend response
      if (response.data.paymentStatus === 'paid' || response.data.paymentStatus === 'completed') {
        setStatus('success');
      } else if (response.data.paymentStatus === 'failed') {
        setStatus('failed');
      } else if (response.data.paymentStatus === 'processing') {
        setStatus('pending');
      } else {
        setStatus('unknown');
      }

    } catch (error) {
      console.error('❌ Error verifying payment:', error);
      setStatus('error');
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleNewBooking = () => {
    navigate('/booking');
  };

  const handleRetryPayment = () => {
    // Redirect back to booking with the booking ID to retry payment
    navigate(`/booking?retry=${bookingId}`);
  };

  // CHECKING STATUS
  if (status === 'checking') {
    return (
      <div className="payment-result-container">
        <div className="payment-card">
          <div className="spinner"></div>
          <h2>Se verifică plata...</h2>
          <p>Vă rugăm așteptați...</p>
        </div>
      </div>
    );
  }

  // SUCCESS STATUS
  if (status === 'success') {
    return (
      <div className="payment-result-container">
        <div className="payment-card success">
          <div className="icon-circle success">
            <span className="icon">✓</span>
          </div>
          <h1>Plată Reușită! 🎉</h1>
          <p className="booking-id">Cod rezervare: <strong>{bookingId}</strong></p>
          
          <div className="message-box success-box">
            <h3>✅ Plata a fost procesată cu succes!</h3>
            <ul className="success-list">
              <li>📧 Veți primi un email de confirmare în câteva minute</li>
              <li>📞 Un reprezentant vă va contacta în maximum 48 de ore pentru confirmare finală</li>
              <li>🎿 Rezervarea dumneavoastră este confirmată</li>
            </ul>
            {paymentDetails?.paymentData?.transactionId && (
              <p className="transaction-id">
                ID Tranzacție: <code>{paymentDetails.paymentData.transactionId}</code>
              </p>
            )}
            {paymentDetails?.totalPrice && (
              <p className="payment-amount">
                Sumă plătită: <strong>{paymentDetails.totalPrice} RON</strong>
              </p>
            )}
          </div>

          <div className="next-steps">
            <h3 className="next-steps-title">📋 Pașii următori:</h3>
            <ul className="steps-list">
              <li>Verificați emailul pentru detalii complete ale rezervării</li>
              <li>Pregătiți echipamentul (schiuri sau snowboard)</li>
              <li>Prezentați-vă cu <strong>10 minute înainte</strong> de ora lecției</li>
              <li>Nu uitați skipass-ul (nu este inclus în tarif)</li>
            </ul>
          </div>

          <div className="contact-info">
            <p className="contact-text">
              <strong>Pentru întrebări sau modificări:</strong>
              <br />
              📞 0745 573 126
              <br />
              📧 contact@schisport.ro
            </p>
          </div>

          <div className="actions">
            <button className="btn-primary" onClick={handleBackToHome}>
              🏠 Înapoi la Pagina Principală
            </button>
            <button className="btn-secondary" onClick={handleNewBooking}>
              ➕ Rezervare Nouă
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PENDING STATUS
  if (status === 'pending') {
    return (
      <div className="payment-result-container">
        <div className="payment-card warning">
          <div className="icon-circle warning">
            <span className="icon">⏳</span>
          </div>
          <h1>Plată în Procesare</h1>
          <p className="booking-id">Cod rezervare: <strong>{bookingId}</strong></p>
          
          <div className="message-box warning-box">
            <h3>⏳ Plata este în curs de procesare</h3>
            <p>Acest proces poate dura câteva minute.</p>
            <p>📧 Veți primi un email când plata va fi confirmată.</p>
            <p>📞 Puteți contacta suportul pentru detalii: <strong>0745 573 126</strong></p>
          </div>

          <div className="actions">
            <button className="btn-primary" onClick={() => verifyPayment()}>
              🔄 Verifică Din Nou
            </button>
            <button className="btn-secondary" onClick={handleBackToHome}>
              🏠 Înapoi la Pagina Principală
            </button>
          </div>
        </div>
      </div>
    );
  }

  // FAILED STATUS
  if (status === 'failed') {
    return (
      <div className="payment-result-container">
        <div className="payment-card error">
          <div className="icon-circle error">
            <span className="icon">✗</span>
          </div>
          <h1>Plată Eșuată</h1>
          <p className="booking-id">Cod rezervare: <strong>{bookingId}</strong></p>
          
          <div className="message-box error-box">
            <h3>❌ Plata nu a putut fi procesată</h3>
            <p>Posibile motive:</p>
            <ul className="error-list">
              <li>Fonduri insuficiente pe card</li>
              <li>Date de card incorecte</li>
              <li>Tranzacția a fost respinsă de bancă</li>
            </ul>
            <p><strong>Ce puteți face:</strong></p>
            <ul className="action-list">
              <li>Încercați din nou cu alt card</li>
              <li>Verificați datele cardului</li>
              <li>Alegeți plata cash la locație</li>
            </ul>
          </div>

          <div className="contact-info">
            <p className="contact-text">
              📞 Pentru asistență: <strong>0745 573 126</strong>
            </p>
          </div>

          <div className="actions">
            <button className="btn-primary" onClick={handleRetryPayment}>
              🔄 Încearcă Din Nou
            </button>
            <button className="btn-secondary" onClick={handleBackToHome}>
              🏠 Înapoi la Pagina Principală
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ERROR/UNKNOWN STATUS
  return (
    <div className="payment-result-container">
      <div className="payment-card error">
        <div className="icon-circle error">
          <span className="icon">?</span>
        </div>
        <h1>Eroare la Verificare</h1>
        <p className="booking-id">Cod rezervare: <strong>{bookingId || 'Necunoscut'}</strong></p>
        
        <div className="message-box error-box">
          <h3>⚠️ Nu am putut verifica statusul plății</h3>
          <p>Vă rugăm să ne contactați pentru verificare manuală:</p>
          <p className="contact-highlight">
            📞 <strong>0745 573 126</strong>
          </p>
        </div>

        <div className="actions">
          <button className="btn-primary" onClick={handleBackToHome}>
            🏠 Înapoi la Pagina Principală
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;